import { useState, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { useAuth } from './useAuth';

// Pro features that require subscription
export const PRO_FEATURES = {
  GOALS: 'goals',
  ONE_ON_ONES: 'oneOnOnes',
  WEEKLY_REVIEW: 'weeklyReview',
  GOOGLE_CALENDAR: 'googleCalendar',
  GMAIL: 'gmail',
  SLACK: 'slack',
  SIRI_SHORTCUTS: 'siriShortcuts',
};

// Feature metadata for UI
export const FEATURE_INFO = {
  [PRO_FEATURES.GOALS]: {
    name: 'Goals & Projects',
    description: 'Set and track long-term goals with project organization',
    icon: 'target',
  },
  [PRO_FEATURES.ONE_ON_ONES]: {
    name: '1:1 Meetings',
    description: 'Manage 1:1 meetings with your team',
    icon: 'users',
  },
  [PRO_FEATURES.WEEKLY_REVIEW]: {
    name: 'Weekly Review',
    description: 'Complete weekly reviews to stay on track',
    icon: 'calendar-check',
  },
  [PRO_FEATURES.GOOGLE_CALENDAR]: {
    name: 'Google Calendar Sync',
    description: 'Sync your schedule with Google Calendar',
    icon: 'calendar',
  },
  [PRO_FEATURES.GMAIL]: {
    name: 'Gmail Capture',
    description: 'Capture tasks from labeled emails',
    icon: 'mail',
  },
  [PRO_FEATURES.SLACK]: {
    name: 'Slack Integration',
    description: 'Add tasks from Slack with /inbox command',
    icon: 'message-square',
  },
  [PRO_FEATURES.SIRI_SHORTCUTS]: {
    name: 'Siri Shortcuts',
    description: 'Quick-add tasks using Siri',
    icon: 'mic',
  },
};

export function useSubscription() {
  const { userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Subscription status derived from user profile
  const subscriptionStatus = useMemo(() => {
    if (!userProfile) {
      return {
        status: 'free',
        tier: 'free',
        plan: null,
        hasProAccess: false,
        isTrialing: false,
        hasLifetimeAccess: false,
        trialDaysRemaining: null,
        canStartTrial: true,
        displayStatus: 'Free',
      };
    }

    const {
      subscriptionStatus: status = 'free',
      subscriptionTier = 'free',
      subscriptionPlan = null,
      proFeaturesEnabled = false,
      lifetimeAccess = false,
      trialUsed = false,
      trialEndsAt,
      cancelAtPeriodEnd = false,
      stripeCustomerId = null,
    } = userProfile;

    // Calculate trial days remaining
    let trialDaysRemaining = null;
    const isTrialing = status === 'trialing';
    if (isTrialing && trialEndsAt) {
      const trialEnd = trialEndsAt.toDate ? trialEndsAt.toDate() : new Date(trialEndsAt);
      const now = new Date();
      trialDaysRemaining = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
    }

    // Has Pro access if lifetime or proFeaturesEnabled
    const hasProAccess = lifetimeAccess || proFeaturesEnabled;

    // Has an actual Stripe subscription (not just trial)
    const hasStripeSubscription = !!stripeCustomerId && (status === 'active' || status === 'past_due');

    // Can start trial if not used and not already subscribed
    const canStartTrial = !trialUsed && status !== 'active' && !lifetimeAccess;

    // Display status
    let displayStatus = 'Free';
    if (lifetimeAccess) {
      displayStatus = 'Lifetime Pro';
    } else if (status === 'trialing') {
      displayStatus = trialDaysRemaining !== null ? `Trial (${trialDaysRemaining} days left)` : 'Trial';
    } else if (status === 'active') {
      displayStatus = cancelAtPeriodEnd ? 'Pro (Canceling)' : 'Pro';
    } else if (status === 'past_due') {
      displayStatus = 'Pro (Payment Issue)';
    }

    return {
      status,
      tier: subscriptionTier,
      plan: subscriptionPlan,
      hasProAccess,
      hasStripeSubscription,
      isTrialing,
      hasLifetimeAccess: lifetimeAccess,
      trialDaysRemaining,
      canStartTrial,
      displayStatus,
      cancelAtPeriodEnd,
    };
  }, [userProfile]);

  // Check if user has access to a Pro feature
  const hasAccess = useCallback((feature) => {
    return subscriptionStatus.hasProAccess;
  }, [subscriptionStatus.hasProAccess]);

  // Start free trial
  const startFreeTrial = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const startTrial = httpsCallable(functions, 'startFreeTrial');
      const result = await startTrial();
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create checkout session
  const createCheckoutSession = useCallback(async (plan) => {
    setIsLoading(true);
    setError(null);

    try {
      const createSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createSession({
        plan,
        successUrl: `${window.location.origin}?subscription=success`,
        cancelUrl: `${window.location.origin}?subscription=canceled`,
      });
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create billing portal session
  const createBillingPortalSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const createSession = httpsCallable(functions, 'createBillingPortalSession');
      const result = await createSession({
        returnUrl: `${window.location.origin}/settings`,
      });
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Redeem promo code
  const redeemPromoCode = useCallback(async (code) => {
    setIsLoading(true);
    setError(null);

    try {
      const redeem = httpsCallable(functions, 'redeemPromoCode');
      const result = await redeem({ code });
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Open checkout in new tab
  const openCheckout = useCallback(async (plan) => {
    try {
      const { url } = await createCheckoutSession(plan);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open checkout:', err);
    }
  }, [createCheckoutSession]);

  // Open billing portal in new tab
  const openBillingPortal = useCallback(async () => {
    try {
      const { url } = await createBillingPortalSession();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    }
  }, [createBillingPortalSession]);

  return {
    // Status
    ...subscriptionStatus,

    // Feature access
    hasAccess,

    // Actions
    startFreeTrial,
    createCheckoutSession,
    createBillingPortalSession,
    redeemPromoCode,
    openCheckout,
    openBillingPortal,

    // Loading/Error state
    isLoading,
    error,
    clearError: () => setError(null),
  };
}
